import { ContentCopy, ExpandMore } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { memo, useMemo } from "react";
import { TableComponents, TableVirtuoso } from "react-virtuoso";

const JSONView = styled("pre")(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#343434" : "white",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  fontSize: theme.typography.body2.fontSize,
  textWrap: "wrap",
  overflow: "auto",
  maxHeight: "500px",
}));

interface AnswerDebugDetailProps {
  debugValue: any;
}

/**
 * Parse CSV string and extract totals JSON
 */
const parseCSVAndTotals = (csvString: string) => {
  const totalsMarker = ">>>>>>>>> Totals <<<<<<<<<<";
  const totalsIndex = csvString.indexOf(totalsMarker);

  let csvPart = csvString;
  let totalsJson = null;

  if (totalsIndex !== -1) {
    csvPart = csvString.substring(0, totalsIndex);
    const totalsString = csvString.substring(totalsIndex + totalsMarker.length).trim();

    // Try to parse the JSON totals
    try {
      // Remove escaped characters and newlines
      let cleanedJson = totalsString.replace(/\\n/g, "").replace(/\\/g, "").trim();

      // Convert JavaScript object notation to proper JSON by adding quotes around keys
      cleanedJson = cleanedJson.replace(/(\w+):/g, '"$1":');

      totalsJson = JSON.parse(cleanedJson);
    } catch (e) {
      console.error("Failed to parse totals JSON:", e);
    }
  }

  // Remove escaped characters and split by lines
  const cleanedCSV = csvPart.replace(/\\n/g, "\n").replace(/\\/g, "");
  const lines = cleanedCSV.split("\n").filter((line) => line.trim());

  if (lines.length === 0) return null;

  const headers = lines[0]?.split(",").map((h) => h.trim()) || [];
  const rows = lines.slice(1).map((line) => {
    const values = line.split(",");
    return headers.reduce(
      (obj, header, index) => {
        obj[header] = values[index]?.trim() || "";
        return obj;
      },
      {} as Record<string, string>
    );
  });

  return { headers, rows, totals: totalsJson };
};

interface CSVData {
  [key: string]: string;
}

interface ColumnData {
  dataKey: string;
  label: string;
  numeric?: boolean;
  width?: number;
}

const VirtuosoTableComponents: TableComponents<CSVData> = {
  Scroller: React.forwardRef<HTMLDivElement>((props: any, ref) => (
    <TableContainer component={Paper} {...props} ref={ref} />
  )),
  Table: (props: any) => (
    <Table {...props} sx={{ borderCollapse: "separate", tableLayout: "fixed" }} />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement>((props: any, ref) => (
    <TableHead {...props} ref={ref} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props: any, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

/**
 * Display totals and periodTotals as formatted JSON
 */
const TotalsDisplay = memo(
  ({
    totalsString,
    periodTotalsString,
  }: {
    totalsString: string | null;
    periodTotalsString: string | null;
  }) => {
    const formatJson = (jsonStr: string): string => {
      try {
        // Try to parse and pretty-print
        const parsed = JSON.parse(jsonStr);
        return JSON.stringify(parsed, null, 2);
      } catch {
        // If parsing fails, just return the raw string
        return jsonStr;
      }
    };

    if (!totalsString && !periodTotalsString) return null;

    return (
      <Stack direction="column" spacing={2} mt={2}>
        {totalsString && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Overall Totals:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <pre style={{ margin: 0, fontSize: "0.875rem", overflow: "auto" }}>
                {formatJson(totalsString)}
              </pre>
            </Paper>
          </Box>
        )}

        {periodTotalsString && (
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Period Totals:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <pre style={{ margin: 0, fontSize: "0.875rem", overflow: "auto" }}>
                {formatJson(periodTotalsString)}
              </pre>
            </Paper>
          </Box>
        )}
      </Stack>
    );
  }
);

/**
 * Render CSV data as a table with optional totals
 * Uses react-virtuoso for optimal performance with large datasets
 */
const CSVTable = memo(({ csvString }: { csvString: string }) => {
  // Memoize parsing to prevent re-computation on every render
  const data = useMemo(() => parseCSVAndTotals(csvString), [csvString]);

  const totalsCellSx = useMemo(
    () => ({
      padding: "6px 12px",
      fontSize: "0.8rem",
    }),
    []
  );

  if (!data) {
    return <Typography color="error">Unable to parse CSV data</Typography>;
  }

  const { headers, rows, totals } = data;

  // Create column configuration for react-virtuoso
  const columns: ColumnData[] = headers.map((header) => ({
    width: 150,
    label: header,
    dataKey: header,
  }));

  function fixedHeaderContent() {
    return (
      <TableRow>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric || false ? "right" : "left"}
            style={{ width: column.width }}
            sx={{
              backgroundColor: "background.paper",
              fontWeight: "bold",
              fontSize: "0.75rem",
              padding: "4px 8px",
            }}
          >
            {column.label}
          </TableCell>
        ))}
      </TableRow>
    );
  }

  function rowContent(_index: number, row: CSVData) {
    return (
      <React.Fragment>
        {columns.map((column) => (
          <TableCell
            key={column.dataKey}
            align={column.numeric || false ? "right" : "left"}
            sx={{
              fontSize: "0.75rem",
              padding: "4px 8px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row[column.dataKey]}
          </TableCell>
        ))}
      </React.Fragment>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
        {rows.length.toLocaleString()} rows
      </Typography>
      <Paper style={{ height: 600, width: "100%" }}>
        <TableVirtuoso
          data={rows}
          components={VirtuosoTableComponents}
          fixedHeaderContent={fixedHeaderContent}
          itemContent={rowContent}
        />
      </Paper>
      {totals && (
        <Box mt={2}>
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Totals:
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableBody>
                {Object.entries(totals).map(([key, value]) => (
                  <TableRow key={key} hover>
                    <TableCell sx={{ ...totalsCellSx, fontWeight: "bold", width: "40%" }}>
                      {key}
                    </TableCell>
                    <TableCell sx={totalsCellSx}>
                      {typeof value === "number" ? value.toLocaleString() : String(value)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
});

/**
 * Tools that should render CSV output as tables
 */
const _CSV_TOOLS = [
  "get_composition",
  "get_paid_media_data",
  "get_breakdowns",
  "get_investment_levels",
  "get_brand_equity",
];

/**
 * Convert tool input object to query parameter string
 */
const generateQueryParams = (input: any): string => {
  if (!input || typeof input !== "object") {
    return "";
  }

  const params = new URLSearchParams();

  Object.entries(input).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      // Handle arrays by adding multiple params with the same key
      value.forEach((item) => {
        params.append(key, String(item));
      });
    } else if (value instanceof Date) {
      // Format dates as YYYY-MM-DD
      const dateStr = value.toISOString().split("T")[0];
      if (dateStr) {
        params.append(key, dateStr);
      }
    } else {
      params.append(key, String(value));
    }
  });

  return `?${params.toString()}`;
};

/**
 * Copy text to clipboard
 */
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
    return false;
  }
};

/**
 * Render a single tool output
 */
const ToolOutputItem = ({ tool, index }: { tool: any; index: number }) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopyQueryParams = async () => {
    if (!tool.toolInput) return;

    const queryString = generateQueryParams(tool.toolInput);
    const success = await copyToClipboard(queryString);

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  // Extract csvRows from "rowsCsv:" until the next closing brace (inclusive)
  const rowsCsvStart = tool?.toolOutput?.indexOf("rowsCsv:");
  let csvRows: string | null = null;
  if (rowsCsvStart !== -1) {
    const valueStart = rowsCsvStart + 8; // "rowsCsv:" is 8 characters
    const nextBrace = tool?.toolOutput?.indexOf("}", valueStart);
    if (nextBrace !== -1) {
      csvRows = tool?.toolOutput?.substring(valueStart, nextBrace + 1).trim();
    }
  }

  // Extract pagination from "pagination:" until the next closing brace (inclusive)
  const paginationStart = tool?.toolOutput?.indexOf("pagination:");
  let pagination: string | null = null;
  if (paginationStart !== -1) {
    const valueStart = paginationStart + 11; // "pagination:" is 11 characters
    const nextBrace = tool?.toolOutput?.indexOf("}", valueStart);
    if (nextBrace !== -1) {
      pagination = tool?.toolOutput?.substring(valueStart, nextBrace + 1).trim();
    }
  }

  // Extract totals and periodTotals as JSON strings
  const extractJsonString = (str: string, marker: string): string | null => {
    const markerIndex = str.indexOf(marker);
    if (markerIndex === -1) return null;

    const valueStart = markerIndex + marker.length;
    let depth = 0;
    let inString = false;
    let escapeNext = false;
    let startBrace = -1;

    for (let i = valueStart; i < str.length; i++) {
      const char = str[i];

      if (escapeNext) {
        escapeNext = false;
        continue;
      }

      if (char === "\\") {
        escapeNext = true;
        continue;
      }

      if (char === '"' || char === "'") {
        inString = !inString;
        continue;
      }

      if (inString) continue;

      if (char === "{") {
        if (depth === 0) startBrace = i;
        depth++;
      } else if (char === "}") {
        depth--;
        if (depth === 0 && startBrace !== -1) {
          const jsonStr = str.substring(startBrace, i + 1);
          return jsonStr;
        }
      }
    }
    return null;
  };

  const totalsString = extractJsonString(tool?.toolOutput || "", "totals:");
  const periodTotalsString = extractJsonString(tool?.toolOutput || "", "periodTotals:");

  // console.log("totalsString", totalsString, "periodTotalsString", periodTotalsString);

  const durationText =
    tool.durationMs !== undefined ? ` - ${(tool.durationMs / 1000).toFixed(2)}s` : "";

  return (
    <Accordion
      key={index}
      elevation={0}
      slotProps={{
        transition: {
          timeout: 0,
        },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography>
          {index + 1}. {tool.toolName}
          {durationText}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          {tool.toolInput && (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Input:
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy as query parameters"}>
                  <IconButton size="small" onClick={handleCopyQueryParams} sx={{ ml: "auto" }}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <JSONView>{JSON.stringify(tool.toolInput, null, 2)}</JSONView>
            </Box>
          )}
          <Typography variant="subtitle2" fontWeight="bold" mb={1}>
            Output:
          </Typography>
          {tool?.toolOutput && (
            <Stack direction="column" spacing={2}>
              {csvRows && <CSVTable csvString={csvRows} />}
              {pagination && (
                <>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                    Pagination:
                  </Typography>
                  <JSONView>{pagination}</JSONView>
                </>
              )}
              <TotalsDisplay totalsString={totalsString} periodTotalsString={periodTotalsString} />
            </Stack>
          )}
          {!csvRows && !pagination && !totalsString && !periodTotalsString && tool?.toolOutput && (
            <JSONView>{tool?.toolOutput}</JSONView>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

/**
 * Group tools by toolUseId to combine input and output
 */
const groupToolsByUseId = (tools: any[]) => {
  const grouped = new Map<string, any>();

  tools.forEach((tool) => {
    const id = tool.toolUseId;
    if (!grouped.has(id)) {
      grouped.set(id, {
        toolName: tool.toolName,
        toolUseId: tool.toolUseId,
        modelUsed: tool.modelUsed,
        durationMs: tool.durationMs,
      });
    }

    const existing = grouped.get(id);
    if (tool.toolInput) {
      existing.toolInput = tool.toolInput;
    }
    if (tool?.toolOutput) {
      existing.toolOutput = tool?.toolOutput;
    }
    if (tool.durationMs !== undefined) {
      existing.durationMs = tool.durationMs;
    }
  });

  return Array.from(grouped.values());
};

/**
 * Component that displays debug information for a chat message in an expandable accordion.
 * Used to show internal query details and processing information for troubleshooting.
 */
export const AnswerDebugDetail = ({ debugValue }: AnswerDebugDetailProps) => {
  console.log("debugValue", debugValue);

  const toolOutputs = debugValue?.toolOutput || [];
  const groupedTools = groupToolsByUseId(toolOutputs);

  return (
    <Box m={2} border={"1px solid #e0e0e0"}>
      <Accordion
        elevation={0}
        slotProps={{
          transition: {
            timeout: 0, // prevents long animation when large accordion is collapsed
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography>Query Detail ({groupedTools.length} tools)</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box display="flex" flexDirection="column" gap={1}>
            {groupedTools.length > 0 ? (
              groupedTools.map((tool: any, index: number) => (
                <ToolOutputItem key={index} tool={tool} index={index} />
              ))
            ) : (
              <JSONView>{JSON.stringify(debugValue, null, 2)}</JSONView>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
